import { useCallback } from 'react';
import { 
  LocationData,
  GameNotification
} from '../types';
import { TRANSFORMER_LEVELS, POWERHOUSE_LEVELS, UPS_LEVELS, GENERATOR_LEVELS, GENERATOR_FUEL_REFILL_COST_PER_HOUR, POWER_GRIDS } from '../constants';
import { calculateLocationGrossPower } from '../utils/powerCalculations';

interface UsePowerManagementProps {
  locations: Record<string, LocationData>;
  setLocations: React.Dispatch<React.SetStateAction<Record<string, LocationData>>>;
  money: number;
  setMoney: React.Dispatch<React.SetStateAction<number>>;
  triggerNotification: (message: string, type: GameNotification['type'], duration?: number) => void;
  formatCurrency: (amount: number, decimals?: number) => string;
  getElectricianDiscount: () => number;
}

interface PowerManagementHook {
  buyPowerUpgrade: (locationId: string, upgradeType: 'transformer' | 'powerhouse' | 'ups', isInstall: boolean) => boolean;
  selectPowerSupplier: (locationId: string, supplierId: string) => boolean;
  buyGenerator: (locationId: string) => boolean;
  refuelGenerator: (locationId: string) => boolean;
}

export const usePowerManagement = ({
  locations,
  setLocations,
  money,
  setMoney,
  triggerNotification,
  formatCurrency,
  getElectricianDiscount,
}: UsePowerManagementProps): PowerManagementHook => {
  
  // Function to buy/upgrade power infrastructure
  const buyPowerUpgrade = useCallback((locationId: string, upgradeType: 'transformer' | 'powerhouse' | 'ups', isInstall: boolean = false): boolean => {
    // Get the current location data
    const location = locations[locationId];
    if (!location) {
      triggerNotification('Location not found', 'error');
      return false;
    }
    
    let cost = 0;
    let nextLevel = 0;
    let upgradeDetails: any = null;
    
    // Enforce power chain installation order
    if (upgradeType === 'powerhouse' && isInstall && !location.transformer) {
      triggerNotification('Install a Transformer before the Powerhouse.', 'warning');
      return false;
    }
    if (upgradeType === 'ups' && isInstall && !location.powerhouse?.installed) {
      triggerNotification('Install a Powerhouse before the UPS.', 'warning');
      return false;
    }

    // Determine the cost and next level based on the upgrade type
    if (upgradeType === 'transformer') {
      if (isInstall) {
        // Installation cost for the level 1 transformer
        upgradeDetails = TRANSFORMER_LEVELS[1];
        cost = upgradeDetails.cost;
        nextLevel = 1;
      } else if (location.transformer) {
        // Get the next level details
        const currentLevel = location.transformer.level;
        if (currentLevel >= TRANSFORMER_LEVELS.length - 1) {
          triggerNotification('Transformer is already at maximum level', 'warning');
          return false;
        }
        nextLevel = currentLevel + 1;
        upgradeDetails = TRANSFORMER_LEVELS[nextLevel];
        cost = upgradeDetails.cost;
      } else {
        triggerNotification('No transformer installed', 'error');
        return false;
      }
    } else if (upgradeType === 'powerhouse') {
      if (isInstall) {
        // Installation cost for the level 1 powerhouse
        upgradeDetails = POWERHOUSE_LEVELS[1];
        cost = upgradeDetails.cost;
        nextLevel = 1;
      } else if (location.powerhouse) {
        // Get the next level details
        const currentLevel = location.powerhouse.level;
        if (currentLevel >= POWERHOUSE_LEVELS.length - 1) {
          triggerNotification('Powerhouse is already at maximum level', 'warning');
          return false;
        }
        nextLevel = currentLevel + 1;
        upgradeDetails = POWERHOUSE_LEVELS[nextLevel];
        cost = upgradeDetails.cost;
      } else {
        triggerNotification('No powerhouse installed', 'error');
        return false;
      }
    } else if (upgradeType === 'ups') {
      if (isInstall) {
        // Installation cost for the level 1 UPS
        upgradeDetails = UPS_LEVELS[1];
        cost = upgradeDetails.cost;
        nextLevel = 1;
      } else if (location.ups) {
        // Get the next level details
        const currentLevel = location.ups.level;
        if (currentLevel >= UPS_LEVELS.length - 1) {
          triggerNotification('UPS is already at maximum level', 'warning');
          return false;
        }
        nextLevel = currentLevel + 1;
        upgradeDetails = UPS_LEVELS[nextLevel];
        cost = upgradeDetails.cost;
      } else {
        triggerNotification('No UPS installed', 'error');
        return false;
      }
    }
    
    // Apply electrician discount to power infrastructure purchases
    cost = Math.round(cost * getElectricianDiscount());

    // Check if player has enough money
    if (money < cost) {
      triggerNotification(`Not enough money. Need ${formatCurrency(cost)}`, 'error');
      return false;
    }

    // Update the location with the new/upgraded component
    setLocations(prev => {
      // Create a deep copy of the location we're updating to ensure immutability
      const updatedLocation = JSON.parse(JSON.stringify(prev[locationId]));
      
      if (upgradeType === 'transformer') {
        if (isInstall) {
          updatedLocation.transformer = {
            id: `transformer-${locationId}-${Date.now()}`,
            level: nextLevel,
            capacityKW: upgradeDetails.capacityKW,
            efficiency: upgradeDetails.efficiency,
            cost: upgradeDetails.cost,
            supplierId: 'supplier-standard', // Default supplier
            connected: true
          };
        } else if (updatedLocation.transformer) {
          updatedLocation.transformer = {
            ...updatedLocation.transformer,
            level: nextLevel,
            capacityKW: upgradeDetails.capacityKW,
            efficiency: upgradeDetails.efficiency,
            cost: upgradeDetails.cost
          };
        }
      } else if (upgradeType === 'powerhouse') {
        if (isInstall) {
          updatedLocation.powerhouse = {
            id: `powerhouse-${locationId}-${Date.now()}`,
            level: nextLevel,
            capacityKW: upgradeDetails.capacityKW,
            efficiency: upgradeDetails.efficiency,
            cost: upgradeDetails.cost,
            installed: true
          };
        } else if (updatedLocation.powerhouse) {
          updatedLocation.powerhouse = {
            ...updatedLocation.powerhouse,
            level: nextLevel,
            capacityKW: upgradeDetails.capacityKW,
            efficiency: upgradeDetails.efficiency,
            cost: upgradeDetails.cost
          };
        }
      } else if (upgradeType === 'ups') {
        if (isInstall) {
          updatedLocation.ups = {
            id: `ups-${locationId}-${Date.now()}`,
            level: nextLevel,
            capacityKWh: upgradeDetails.capacityKWh,
            maxOutputKW: upgradeDetails.maxOutputKW,
            chargeLevelKWh: 0, // Start with empty charge
            efficiency: upgradeDetails.efficiency,
            backupTimeMinutes: 30, // Default backup time
            charging: false,
            discharging: false,
            cost: upgradeDetails.cost,
            installed: true
          };
        } else if (updatedLocation.ups) {
          updatedLocation.ups = {
            ...updatedLocation.ups,
            level: nextLevel,
            capacityKWh: upgradeDetails.capacityKWh,
            maxOutputKW: upgradeDetails.maxOutputKW,
            efficiency: upgradeDetails.efficiency,
            cost: upgradeDetails.cost
          };
        }
      }
      
      // Calculate the gross power (total capacity) for the updated location
      const grossPower = calculateLocationGrossPower(updatedLocation, POWER_GRIDS);
      updatedLocation.grossPowerKW = grossPower;
      
      // Update available power (gross power minus used power)
      updatedLocation.availablePowerKW = grossPower - (updatedLocation.usedPower || 0);
      
      // Force a console log to help debug the update
      console.log(`Updated power for ${locationId} after ${upgradeType} ${isInstall ? 'installation' : 'upgrade'}: Gross=${grossPower.toFixed(1)}kW, Available=${updatedLocation.availablePowerKW.toFixed(1)}kW`);
      
      // Create a new locations object to ensure React detects the change
      const newLocations = { ...prev };
      newLocations[locationId] = updatedLocation;
      
      return newLocations;
    });
    
    // Deduct the cost
    setMoney(prev => prev - cost);
    
    // Trigger a notification
    const action = isInstall ? 'Installed' : 'Upgraded';
    triggerNotification(`${action} ${upgradeType} for ${formatCurrency(cost)}`, 'success');
    
    return true;
  }, [locations, money, setMoney, setLocations, triggerNotification, formatCurrency]);

  // Function to select a power supplier for a location
  const selectPowerSupplier = useCallback((locationId: string, supplierId: string): boolean => {
    const location = locations[locationId];
    if (!location) {
      triggerNotification('Location not found', 'error');
      return false;
    }
    
    // Check if the location has a power grid connection
    if (!location.powerGridId) {
      triggerNotification('No power grid connection available', 'error');
      return false;
    }
    
    // Create a completely new locations object to ensure React detects the change
    setLocations(prev => {
      // Create a deep copy of the location we're updating
      const updatedLocation = JSON.parse(JSON.stringify(prev[locationId]));
      
      // Update the power supplier ID
      updatedLocation.selectedPowerSupplierId = supplierId;
      
      // Calculate the gross power (total capacity) for the updated location
      const grossPower = calculateLocationGrossPower(updatedLocation, POWER_GRIDS);
      updatedLocation.grossPowerKW = grossPower;
      
      // Update available power (gross power minus used power)
      updatedLocation.availablePowerKW = grossPower - (updatedLocation.usedPower || 0);
      
      // Create a new locations object to ensure React detects the change
      const newLocations = { ...prev };
      newLocations[locationId] = updatedLocation;
      
      // Force a console log to help debug the update
      console.log(`Updated power for ${locationId}: Gross=${grossPower.toFixed(1)}kW, Available=${updatedLocation.availablePowerKW.toFixed(1)}kW`);
      
      return newLocations;
    });
    
    triggerNotification('Power supplier selected successfully', 'success');
    return true;
  }, [locations, setLocations, triggerNotification]);

  // Install the next generator level (or first install)
  const buyGenerator = useCallback((locationId: string): boolean => {
    const location = locations[locationId];
    if (!location) {
      triggerNotification('Location not found', 'error');
      return false;
    }

    const currentLevel = location.generator?.level ?? 0;
    const nextLevel = currentLevel + 1;
    if (nextLevel >= GENERATOR_LEVELS.length) {
      triggerNotification('Generator is already at maximum level', 'warning');
      return false;
    }
    const spec = GENERATOR_LEVELS[nextLevel];
    const generatorCost = Math.round(spec.cost * getElectricianDiscount());

    if (money < generatorCost) {
      triggerNotification(`Not enough money. Need ${formatCurrency(generatorCost)}`, 'error');
      return false;
    }

    setMoney(prev => prev - generatorCost);
    setLocations(prev => {
      const loc = prev[locationId];
      if (!loc) return prev;
      const generator = {
        id: loc.generator?.id ?? `generator-${locationId}-${Date.now()}`,
        level: nextLevel,
        runtimeHours: spec.runtimeHours,
        fuelCostPerHour: spec.fuelCostPerHour,
        powerOutputKW: spec.powerOutputKW,
        cost: spec.cost,
        installed: true,
        fuelLevelHours: spec.runtimeHours, // arrives with a full tank
        isRunning: false,
      };
      return { ...prev, [locationId]: { ...loc, generator } };
    });

    triggerNotification(`${currentLevel === 0 ? 'Installed' : 'Upgraded'} diesel generator (Level ${nextLevel}) for ${formatCurrency(spec.cost)}`, 'success');
    return true;
  }, [locations, money, setMoney, setLocations, triggerNotification, formatCurrency]);

  // Refill the generator fuel tank
  const refuelGenerator = useCallback((locationId: string): boolean => {
    const location = locations[locationId];
    if (!location?.generator?.installed) {
      triggerNotification('No generator installed', 'error');
      return false;
    }
    const gen = location.generator;
    const missingHours = gen.runtimeHours - gen.fuelLevelHours;
    if (missingHours <= 0.01) {
      triggerNotification('Fuel tank is already full', 'info');
      return false;
    }
    const cost = Math.ceil(missingHours * GENERATOR_FUEL_REFILL_COST_PER_HOUR);
    if (money < cost) {
      triggerNotification(`Not enough money to refuel. Need ${formatCurrency(cost)}`, 'error');
      return false;
    }

    setMoney(prev => prev - cost);
    setLocations(prev => {
      const loc = prev[locationId];
      if (!loc?.generator) return prev;
      return { ...prev, [locationId]: { ...loc, generator: { ...loc.generator, fuelLevelHours: loc.generator.runtimeHours } } };
    });

    triggerNotification(`Refueled generator for ${formatCurrency(cost)}`, 'success');
    return true;
  }, [locations, money, setMoney, setLocations, triggerNotification, formatCurrency]);

  return {
    buyPowerUpgrade,
    selectPowerSupplier,
    buyGenerator,
    refuelGenerator
  };
};
